import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useLocation } from "react-router-dom";
import { Receipt, Calculator, AlertTriangle, Loader2, Hourglass, Gift, Minus, ChevronDown, User } from "lucide-react";
import { toVocativ } from "../../lib/czech.js";
import { apiFetch } from "../../lib/api.js";
import { getRealUser, getViewAsRole } from "../../lib/auth.js";
import { ListovaniMesice } from "../../components/ui.jsx";
import Pill from "../../components/Pill.jsx";

const MONTHS_CS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
function mesicLabel(m) {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || "—";
  const [y, mm] = m.split("-");
  return `${MONTHS_CS[parseInt(mm, 10) - 1]} ${y}`;
}
function fmt(n) {
  const v = Math.round(Number(n) || 0);
  return v.toLocaleString("cs-CZ");
}
// 14. 9. 2026 (fáze 6): listování měsícem šipkami místo dvou selectů.
function posunMesic(m, n) {
  const [y, mm] = m.split("-").map(Number);
  const d = new Date(Date.UTC(y, mm - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function aktualniMesic() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}
// Prvni mesic = cerven 2026 (start systemu vyplat), dal jen aktualni a minule mesice.
function buildMonths() {
  const out = [];
  const now = new Date();
  const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  let y = 2026, m = 6;
  while (true) {
    const key = `${y}-${String(m).padStart(2, "0")}`;
    out.push(key);
    if (key === cur) break;
    m++; if (m > 12) { m = 1; y++; }
    if (y > now.getFullYear() + 1) break;
  }
  return out.reverse();
}

export default function TrenerFakturace() {
  const { activeTrener } = useOutletContext() || {};
  const months = useMemo(buildMonths, []);
  const [mesic, setMesic] = useState(months[0]);
  const [strzeni, setStrzeni] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jumped, setJumped] = useState(false);
  // Trenéři jsou OSVČ partneři → "Moje fakturace"; zaměstnanci (manager/csm) → "Moje výplata".
  // Rozhoduje CESTA (spolehlivé i v náhledu): /trener/fakturace = trenér, /moje-vyplata = zaměstnanec.
  const location = useLocation();
  const jeFakturace = location.pathname.startsWith("/trener");
  const nadpis = jeFakturace ? "Moje fakturace" : "Moje výplata";

  useEffect(() => {
    let alive = true;
    apiFetch("/api/trener-vyplata-uprava")
      .then((r) => r.json())
      .then((j) => { if (alive && j?.ok) setStrzeni(j.strzeni || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams({ self: "1", mesic });
    // Owner v náhledu (napr. nutritionist): backend resolvne osobu podle role z uzivatele.
    const _real = getRealUser();
    const _nahled = _real?.role === "owner" ? getViewAsRole() : null;
    if (activeTrener) params.set("personName", activeTrener);
    else if (_nahled) params.set("viewRole", _nahled);
    apiFetch(`/api/hr-vyplaty?${params.toString()}`)
      .then((r) => r.json())
      .then((j) => {
        if (!alive) return;
        // Default skoč na poslední schválený měsíc (jen jednou, když aktuální není schválený).
        if (!jumped && j?.posledniSchvaleny && !j?.schvaleno && j.posledniSchvaleny !== mesic && months.includes(j.posledniSchvaleny)) {
          setJumped(true);
          setMesic(j.posledniSchvaleny);
          return;
        }
        setData(j?.ok ? j : null);
      })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [mesic, activeTrener]);

  const schvaleno = data?.schvaleno === true;
  const row = data?.row || null;

  return (
    <div className="space-y-4">
      {/* Hlavicka stranky, jednotna s celym portalem (faze 3, 3.9.2026) */}
      <div className="rounded-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 p-4 text-white shadow-glass sm:p-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 sm:h-11 sm:w-11">
            <Receipt className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-xl font-bold leading-tight sm:text-2xl">{nadpis}</h1>
            <p className="mt-1 text-xs leading-snug text-white/75 sm:text-sm">Jen schválené měsíce</p>
          </div>
        </div>
      </div>

      {/* Listovani mesicem. Nabizime JEN schvalene mesice (neschvaleny, napr. aktualni, se nezobrazi). */}
      <div className="glass min-w-0 p-4 sm:p-5">
        {(() => {
          const povolene = (data?.schvaleneMesice || []).filter((m) => typeof m === "string" && /^\d{4}-\d{2}$/.test(m)).sort();
          const min = povolene[0], max = povolene[povolene.length - 1];
          const dnesek = aktualniMesic();
          return (
            <ListovaniMesice
              popis={mesicLabel(mesic)}
              onZpet={() => setMesic(posunMesic(mesic, -1))}
              onVpred={() => setMesic(posunMesic(mesic, 1))}
              zpetVypnuto={!!min && posunMesic(mesic, -1) < min}
              vpredVypnuto={!!max && posunMesic(mesic, 1) > max}
              naDnesek={povolene.includes(dnesek) ? () => setMesic(dnesek) : undefined}
            />
          );
        })()}
      </div>

      {/* Strzeni motivacni slozky */}
      {strzeni.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-rose-800 font-bold mb-2">
            <AlertTriangle className="w-5 h-5" /> Stržená motivační složka sazby
          </div>
          <div className="space-y-2">
            {strzeni.map((s, i) => (
              <div key={i} className="bg-white border border-rose-200 rounded-xl px-3 py-2 text-sm">
                <div className="font-semibold text-rose-800">{mesicLabel(s.mesic)}</div>
                <div className="text-rose-700">{s.duvod || "Bez uvedeného důvodu."}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-rose-500 mt-2">V uvedených měsících se vyplácí jen základní sazba bez motivační složky. Dotazy na klienti@zateckal.cz.</div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : schvaleno && row ? (
        <PayoutCard row={row} sazbaInfo={data.sazbaInfo} detail={data.detail} odecty={data.odecty_verejne} mesic={mesic} isFakturace={jeFakturace} />
      ) : (
        <WaitingCard activeTrener={activeTrener} mesic={mesic} trener={jeFakturace} />
      )}
    </div>
  );
}

function PayoutCard({ row, sazbaInfo, detail, odecty, mesic, isFakturace }) {
  const isTreninky = row.rezim === "treninky";
  // 16.9.2026: trenér bootcampů. Počítá se stejně jako tréninky, jen se tomu
  // říká lekce, protože na lekci chodí celá skupina.
  const isBootcamp = row.rezim === "bootcamp";
  const isHodiny = row.rezim === "hodiny" || row.rezim === "sales";
  const treninky = (detail && detail.treninky) || [];
  const odectyVerejne = Array.isArray(odecty) ? odecty : [];
  const bonusItems = (detail && detail.bonusItems) || [];
  const srazkaItems = (detail && detail.srazkaItems) || [];
  const [bonusOpen, setBonusOpen] = useState(false);
  const [srazkaOpen, setSrazkaOpen] = useState(false);
  const celkemLabel = isFakturace ? "Celkem k fakturaci" : "Celkem k výplatě";
  // NET k fakturaci (po odečtech vč. skrytých); fallback na celkem, kdyby chybělo.
  const celkoveCislo = (row.k_fakturaci != null) ? row.k_fakturaci : row.celkem;
  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Celkem */}
      <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg p-5 sm:p-6 text-white">
        <div className="text-sm sm:text-base font-semibold text-white/80 mb-1">{celkemLabel} za {mesicLabel(mesic)}</div>
        <div className="text-3xl sm:text-4xl font-black demo-sensitive">{fmt(celkoveCislo)} Kč</div>
      </div>

      {/* Rozpad */}
      <div className="glass min-w-0 space-y-3 p-4 sm:p-5">
        <div className="text-base font-bold text-slate-800 flex items-center gap-2"><Calculator className="w-5 h-5 text-slate-500" /> Rozpad</div>

        {isTreninky && (
          // Přípočet za bootcampové lekce je v základu taky, proto se tady
          // odečte a ukáže se vlastním řádkem níž (16.9.2026).
          <Line label={<>Tréninky <Pill tone="purple">{row.jednotky != null ? row.jednotky : 0}</Pill> <Pill tone="slate">{fmt(row.sazba)} Kč za trénink</Pill></>}
            value={row.zakladni_castka - ((row.bootcamp && row.bootcamp.castka) || 0)} />
        )}
        {row.bootcamp && row.bootcamp.lekci > 0 && (
          <Line label={<>Bootcamp lekce <Pill tone="purple">{row.bootcamp.lekci}</Pill> <Pill tone="slate">{fmt(row.bootcamp.sazba)} Kč za lekci</Pill></>}
            value={row.bootcamp.castka} />
        )}
        {isBootcamp && (
          <Line label={<>Lekce bootcampu <Pill tone="purple">{row.jednotky != null ? row.jednotky : 0}</Pill> <Pill tone="slate">{fmt(row.sazba)} Kč za lekci</Pill></>}
            value={row.zakladni_castka} />
        )}
        {row.fix && (
          <Line label="Fixní odměna" value={row.zakladni_castka} />
        )}
        {isHodiny && (row.hodiny_rozpad || []).map((h, i) => (
          <Line key={i} label={`${h.oblast || "Hodiny"} (${h.hours || 0} h)`} note={`${fmt(h.sazba)} Kč/h`} value={h.castka} />
        ))}

        {row.motivace_strzena && (
          <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
            Tento měsíc je stržená motivační složka sazby (vyplácí se základ {fmt(row.sazba)} Kč místo {fmt(row.sazba_plna)} Kč).
            {row.motivace_duvod ? ` Důvod: ${row.motivace_duvod}` : ""}
          </div>
        )}

        {/* Bonusy — rozklikávací (po řádcích za co jsou) */}
        {row.bonusy > 0 && (
          <div>
            <button type="button" onClick={() => bonusItems.length && setBonusOpen((o) => !o)}
              className={`w-full flex items-center justify-between gap-2 text-left ${bonusItems.length ? "cursor-pointer" : "cursor-default"}`}>
              <span className="text-base text-slate-700 flex items-center gap-1.5 min-w-0">
                <Gift className="w-4 h-4 text-emerald-500 shrink-0" />Bonusy
                {bonusItems.length > 0 && <ChevronDown className={`w-4 h-4 text-slate-400 transition ${bonusOpen ? "rotate-180" : ""}`} />}
              </span>
              <span className="text-base font-semibold text-emerald-700 whitespace-nowrap">{fmt(row.bonusy)} Kč</span>
            </button>
            {bonusOpen && bonusItems.length > 0 && (
              <div className="mt-2 space-y-2">
                {bonusItems.map((b, i) => (
                  <PolozkaRozpadu key={i} ton="bonus" nazev={b.popis || "Bonus"} klient={b.klient}
                    castka={<span className="font-semibold text-emerald-700 whitespace-nowrap">{fmt(b.castka)} Kč</span>} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Srážky — také rozklikávací */}
        {row.srazky > 0 && (
          <div>
            <button type="button" onClick={() => srazkaItems.length && setSrazkaOpen((o) => !o)}
              className={`w-full flex items-center justify-between gap-2 text-left ${srazkaItems.length ? "cursor-pointer" : "cursor-default"}`}>
              <span className="text-base text-slate-700 flex items-center gap-1.5 min-w-0">
                <Minus className="w-4 h-4 text-slate-400 shrink-0" />Srážky
                {srazkaItems.length > 0 && <ChevronDown className={`w-4 h-4 text-slate-400 transition ${srazkaOpen ? "rotate-180" : ""}`} />}
              </span>
              <span className="text-base font-semibold text-rose-600 whitespace-nowrap">−{fmt(row.srazky)} Kč</span>
            </button>
            {srazkaOpen && srazkaItems.length > 0 && (
              <div className="mt-2 space-y-2">
                {srazkaItems.map((s, i) => (
                  <PolozkaRozpadu key={i} ton="srazka" nazev={s.popis || "Srážka"} klient={s.klient}
                    castka={<span className="font-semibold text-rose-600 whitespace-nowrap">−{fmt(s.castka)} Kč</span>} />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="border-t border-slate-200 pt-3 flex items-center justify-between gap-2">
          <div className="text-lg font-bold text-slate-900">{celkemLabel}</div>
          <div className="text-lg sm:text-xl font-black text-emerald-700 demo-sensitive whitespace-nowrap">{fmt(celkoveCislo)} Kč</div>
        </div>
      </div>

      {/* Vyplaceno jinak — veřejné odečty (owner je označil jako viditelné) */}
      {odectyVerejne.length > 0 && (
        <div className="glass min-w-0 p-4">
          <div className="mb-2 flex items-center gap-2.5 rounded-xl bg-brand-100 px-3 py-2"><span className="min-w-0 flex-1 text-sm font-bold text-brand-950">Vyplaceno jinak, fakturou nebo hotově</span></div>
          <div className="space-y-1.5">
            {odectyVerejne.map((o, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                <div className="text-slate-700">{o.popis}</div>
                <div className="font-semibold text-slate-900">{fmt(o.castka)} Kč</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-slate-400 mt-2">Tyto částky ti chodí jiným kanálem (faktura nebo hotově). Jsou už odečtené od částky k fakturaci výše.</div>
        </div>
      )}

      {/* Treninky */}
      {treninky.length > 0 && (
        <div className="glass min-w-0 p-4">
          <div className="mb-2 flex items-center gap-2.5 rounded-xl bg-brand-100 px-3 py-2"><span className="min-w-0 flex-1 text-sm font-bold text-brand-950">{isBootcamp ? "Odvedené lekce" : "Odtrénované tréninky"}</span><span className="shrink-0 text-sm font-bold tabular-nums text-brand-800">{treninky.length}</span></div>
          <div className="space-y-1 max-h-64 overflow-auto">
            {treninky.map((t, i) => (
              <div key={i} className="flex items-center justify-between gap-3 border-b border-slate-100 py-2 last:border-0">
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold tabular-nums text-slate-600">{t.datum}</span>
                  <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">{t.klient}</span>
                  {t.propadle && (
                    <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-amber-800">Propadlý</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// 3. 11. 2026: název položky je přesně text do faktury (bez jména klientky).
// Za jakou klientku bonus nebo srážka je, ukazuje pilulka pod názvem.
// Každá položka je samostatná bublina (karta), ať se řádky nepletou.
function PolozkaRozpadu({ nazev, klient, castka, ton = "bonus" }) {
  const ramecek = ton === "srazka" ? "border-rose-100 bg-rose-50/40" : "border-emerald-100 bg-emerald-50/40";
  return (
    <div className={`flex items-start justify-between gap-3 rounded-2xl border px-3.5 py-2.5 text-sm ${ramecek}`}>
      <div className="min-w-0">
        <div className="text-slate-600 break-words">{nazev}</div>
        {klient && (
          <Pill tone="purple" className="mt-1 gap-1 font-semibold select-none" title="Klientka, nepatří do textu faktury">
            <User className="h-3 w-3 shrink-0" />{klient}
          </Pill>
        )}
      </div>
      {castka}
    </div>
  );
}

function Line({ label, note, value, positive, icon: Icon }) {
  return (
    <div className="flex items-start justify-between gap-3 text-base">
      <div className="text-slate-700 flex items-center gap-1.5 flex-wrap min-w-0">
        {Icon && <Icon className="w-4 h-4 text-slate-400 shrink-0" />}
        <span className="break-words">{label}</span>
        {note && <span className="text-xs sm:text-sm text-slate-400">, {note}</span>}
      </div>
      <div className={`font-semibold whitespace-nowrap ${positive ? "text-emerald-700" : "text-slate-900"}`}>{fmt(value)} Kč</div>
    </div>
  );
}

function WaitingCard({ activeTrener, mesic, trener = true }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
      <Hourglass className="w-10 h-10 text-amber-500 mx-auto mb-3" />
      <div className="text-lg font-bold text-amber-900 mb-1">
        Výplata za {mesicLabel(mesic)} ještě nebyla schválena
      </div>
      <div className="text-sm text-amber-700 max-w-md mx-auto">
        {/* 1. 10. 2026: Moje výplata (sales, výživová, manažer) nemá trenérské oslovení ani jednotky. */}
        {trener
          ? <>{toVocativ(activeTrener) || "Trenére"}, jakmile bude výplata za tento měsíc schválená, uvidíš tady kompletní rozpad: sazba × odvedené jednotky, bonusy a celková částka. Dotazy na klienti@zateckal.cz.</>
          : <>Jakmile bude výplata za tento měsíc schválená, uvidíš tady kompletní rozpad a celkovou částku. Dotazy na klienti@zateckal.cz.</>}
      </div>
    </div>
  );
}
